import React from 'react';
import type { AI5WHAnalysis, AIObligationMapping, AIRecommendation } from '../services/aiService';
import { useTerminology } from '../hooks/useTerminology';

/**
 * AI Assistant UI kit
 *
 * Reusable presentational components for surfacing AI analysis, pre-fills,
 * and recommendations inside the Assessment form. Styled via index.css.
 */

type ConfidenceLevel = 'high' | 'medium' | 'low';

const levelFor = (pct: number): ConfidenceLevel =>
  pct >= 85 ? 'high' : pct >= 70 ? 'medium' : 'low';

export const AIBadge: React.FC = () => <span className="ai-badge">AI</span>;

export const AIConfidence: React.FC<{ value: number; label?: string }> = ({ value, label }) => (
  <span className="ai-confidence">
    <span className={`conf-dot ${levelFor(value) === 'high' ? '' : levelFor(value)}`} />
    {label ? `${label} · ` : ''}
    {Math.round(value)}% confidence
  </span>
);

export const AIInlineConfidence: React.FC<{ value: number; label?: string }> = ({
  value,
  label = 'AI pre-filled',
}) => (
  <span className="ai-inline-confidence">
    <span className={`conf-dot ${levelFor(value) === 'high' ? '' : levelFor(value)}`} />
    {label} · {Math.round(value)}%
  </span>
);

export const AIProcessing: React.FC<{ text?: string }> = ({ text = 'AI is analysing…' }) => (
  <span className="ai-processing">
    <span className="ai-spinner" />
    {text}
  </span>
);

export const AITriggerBar: React.FC<{
  title: string;
  subtitle: string;
  ctaLabel: string;
  processing?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ title, subtitle, ctaLabel, processing, disabled, onClick }) => (
  <div className="ai-trigger-bar">
    <div className="ai-trigger-icon">✦</div>
    <div className="ai-trigger-text">
      <div className="ai-trigger-title">{title}</div>
      <div className="ai-trigger-sub">{subtitle}</div>
    </div>
    <button
      type="button"
      className="ai-trigger-btn"
      onClick={onClick}
      disabled={disabled || processing}
    >
      {processing ? 'Processing…' : ctaLabel}
    </button>
  </div>
);

export const AIPanel: React.FC<{
  title: string;
  confidence: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, confidence, children, style }) => (
  <div className="ai-panel" style={style}>
    <div className="ai-panel-header">
      <AIBadge />
      <span className="ai-panel-title">{title}</span>
      <AIConfidence value={confidence} />
    </div>
    <div className="ai-panel-body">{children}</div>
  </div>
);

type Dimension = 'who' | 'what' | 'where' | 'when' | 'why' | 'how';

const DIMENSIONS: Array<{ key: Dimension; label: string }> = [
  { key: 'who', label: 'WHO' },
  { key: 'what', label: 'WHAT' },
  { key: 'where', label: 'WHERE' },
  { key: 'when', label: 'WHEN' },
  { key: 'why', label: 'WHY' },
  { key: 'how', label: 'HOW' },
];

export const AIAnalysisResult: React.FC<{
  analysis: AI5WHAnalysis;
  obligations?: AIObligationMapping;
}> = ({ analysis, obligations }) => {
  const t = useTerminology();
  return (
  <>
    <AIPanel title="5W+H Analysis Complete" confidence={analysis.confidence}>
      <div className="ai-5wh-grid">
        {DIMENSIONS.map(({ key, label }) => {
          const dim = analysis[key];
          const ok = !!dim?.present;
          return (
            <div key={key} className="ai-5wh-cell" title={dim?.value || ''}>
              <span className={`dim-tag tag-${key}`}>{label}</span>
              <span className={`dim-status ${ok ? 'ok' : 'missing'}`}>{ok ? '✓' : '✕'}</span>
              {dim?.value && <span className="dim-value">{truncate(dim.value, 38)}</span>}
            </div>
          );
        })}
      </div>
      <div>
        <span className="ai-score-pill">
          Score <strong>{analysis.score}/6</strong>
        </span>
        {analysis.summary}
      </div>
    </AIPanel>
    {obligations && (
      <AIPanel title={`${t.req_field_label} Mapping`} confidence={obligations.confidence} style={{ marginTop: 10 }}>
        <div className="ai-obligations-list">
          {obligations.items.map((it, i) => (
            <div key={i} className="ai-obligation-item">
              <strong>{it.name}</strong>
              <span className="ob-id">{it.id}</span>
              <span style={{ flexBasis: '100%', fontSize: 11, color: '#4b5563' }}>
                {it.rationale}
              </span>
              <span className={`ob-rec ${it.recommendation.toLowerCase()}`}>
                {it.recommendation}
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 11, opacity: 0.9 }}>{obligations.note}</div>
      </AIPanel>
    )}
  </>
  );
};

function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;
}

export const AIRecommendationPanel: React.FC<{
  recommendation: AIRecommendation;
  onUse: (text: string) => void;
  onEdit?: (text: string) => void;
  onDismiss: () => void;
  used?: boolean;
  dismissed?: boolean;
}> = ({ recommendation, onUse, onEdit, onDismiss, used, dismissed }) => {
  if (dismissed) return null;
  return (
    <div className="ai-rec-panel">
      <div className="ai-rec-header">
        <span className="ai-badge">AI</span>
        <span className="ai-rec-title">
          AI suggestion — {recommendation.dimension}
        </span>
        <AIConfidence value={recommendation.confidence} />
        <button type="button" className="ai-rec-dismiss" onClick={onDismiss} aria-label="Dismiss">
          ✕
        </button>
      </div>
      <div className="ai-rec-body">{recommendation.suggestion}</div>
      <div className="ai-rec-footer">
        {used ? (
          <span className="ai-rec-used">✓ Copied to field</span>
        ) : (
          <>
            <button
              type="button"
              className="ai-rec-use"
              onClick={() => onUse(recommendation.suggestion)}
            >
              Use suggestion
            </button>
            {onEdit && (
              <button
                type="button"
                className="ai-rec-edit"
                onClick={() => onEdit(recommendation.suggestion)}
              >
                Edit before using
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const AIOverrideNotice: React.FC<{ show: boolean }> = ({ show }) =>
  show ? (
    <div className="ai-override-notice">
      You have changed the AI pre-filled answer. Please explain why in the comments — this will
      be recorded in the audit trail.
    </div>
  ) : null;
